import { useMutation, useQueryClient } from '@tanstack/react-query';

import { amendmentQueryKeys, PAYRUN_QUERY_KEYS } from '@xemplo/gp-constants';
import {
  AmendmentCreate,
  CreateTerminationPayload,
  EditAmendmentPayload,
  EditPayInstructionPayload,
  EditTerminationPayload,
  LegacyListPagedResult,
  PayInstructionPayload,
} from '@xemplo/gp-types';
import { useHttpClient } from '@xemplo/http';
import { apiUrlBuilder } from '@xemplo/url-helper';
import { useAppSettings } from '@xemplo/xemplo-app-settings';

import { AmendmentAPIV2 } from './amendment-query.constants';
import { Id } from './amendment-query.types';

export const useEditAmendment = ({ amendmentId }: { amendmentId: Id }) => {
  const { applicationApiUrl } = useAppSettings();
  const { client } = useHttpClient();
  const queryClient = useQueryClient();
  const url = apiUrlBuilder(AmendmentAPIV2.editAmendment(amendmentId), applicationApiUrl);

  return useMutation({
    mutationFn: async (payload: Partial<EditAmendmentPayload>) => {
      await client.patch(url, payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [PAYRUN_QUERY_KEYS.GET_PAYRUN_AMENDMENTS],
      });
      queryClient.invalidateQueries({ queryKey: amendmentQueryKeys.lists() });
      queryClient.cancelQueries({
        queryKey: amendmentQueryKeys.detailsById(String(amendmentId)),
      });
      queryClient.invalidateQueries({
        queryKey: amendmentQueryKeys.detailsById(String(amendmentId)),
      });
    },
  });
};

export const useEditTermination = ({ amendmentId }: { amendmentId: Id }) => {
  const { applicationApiUrl } = useAppSettings();
  const { client } = useHttpClient();
  const queryClient = useQueryClient();
  const url = apiUrlBuilder(
    AmendmentAPIV2.editTerminationAmendment(amendmentId),
    applicationApiUrl,
  );

  return useMutation({
    mutationFn: async (payload: Partial<EditTerminationPayload>) => {
      await client.patch(url, payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [PAYRUN_QUERY_KEYS.GET_PAYRUN_AMENDMENTS],
      });
      queryClient.invalidateQueries({ queryKey: amendmentQueryKeys.lists() });
      queryClient.cancelQueries({
        queryKey: amendmentQueryKeys.detailsById(String(amendmentId)),
      });
      queryClient.invalidateQueries({
        queryKey: amendmentQueryKeys.detailsById(String(amendmentId)),
      });
    },
  });
};

export const useEditPayInstruction = ({ amendmentId }: { amendmentId: Id }) => {
  const { applicationApiUrl } = useAppSettings();
  const { client } = useHttpClient();
  const queryClient = useQueryClient();
  const url = apiUrlBuilder(
    AmendmentAPIV2.editPayInstructionAmendment(amendmentId),
    applicationApiUrl,
  );

  return useMutation({
    mutationFn: async (payload: EditPayInstructionPayload) => {
      const formData = new FormData();
      Object.entries(payload).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
          formData.append(key, value);
        }
      });
      await client.patch(url, formData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [PAYRUN_QUERY_KEYS.GET_PAYRUN_AMENDMENTS],
      });
      queryClient.invalidateQueries({ queryKey: amendmentQueryKeys.lists() });
      queryClient.cancelQueries({
        queryKey: amendmentQueryKeys.detailsById(String(amendmentId)),
      });
      queryClient.invalidateQueries({
        queryKey: amendmentQueryKeys.detailsById(String(amendmentId)),
      });
    },
  });
};

export const useCreateAmendmentV2 = () => {
  const { applicationApiUrl } = useAppSettings();
  const { client } = useHttpClient();
  const queryClient = useQueryClient();
  const url = apiUrlBuilder(AmendmentAPIV2.createAmendment, applicationApiUrl);

  return useMutation({
    mutationFn: async (payload: AmendmentCreate) => {
      await client.post(url, payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [PAYRUN_QUERY_KEYS.GET_PAYRUN_AMENDMENTS],
      });
      queryClient.invalidateQueries({ queryKey: amendmentQueryKeys.lists() });
      queryClient.invalidateQueries({
        queryKey: [PAYRUN_QUERY_KEYS.GET_PAYRUN_WORKERS],
      });
    },
  });
};

export const useCreatePayInstructionV2 = () => {
  const { applicationApiUrl } = useAppSettings();
  const { client } = useHttpClient();
  const queryClient = useQueryClient();
  const url = apiUrlBuilder(AmendmentAPIV2.createPayInstruction, applicationApiUrl);

  return useMutation({
    mutationFn: async (payload: PayInstructionPayload) => {
      const formData = new FormData();
      Object.entries(payload).forEach(([key, value]) => {
        if (value) {
          formData.append(key, value);
        }
      });
      await client.post(url, formData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [PAYRUN_QUERY_KEYS.GET_PAYRUN_AMENDMENTS],
      });
      queryClient.invalidateQueries({ queryKey: amendmentQueryKeys.lists() });
      queryClient.invalidateQueries({
        queryKey: [PAYRUN_QUERY_KEYS.GET_PAYRUN_WORKERS],
      });
    },
  });
};

export const useCreateTerminationV2 = () => {
  const { applicationApiUrl } = useAppSettings();
  const { client } = useHttpClient();
  const queryClient = useQueryClient();
  const url = apiUrlBuilder(AmendmentAPIV2.createTermination, applicationApiUrl);

  return useMutation({
    mutationFn: (payload: CreateTerminationPayload) => {
      return client
        .post<LegacyListPagedResult<string>>(url, payload)
        .then((resp) => resp.data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [PAYRUN_QUERY_KEYS.GET_PAYRUN_AMENDMENTS],
      });
      queryClient.invalidateQueries({ queryKey: amendmentQueryKeys.lists() });
      queryClient.invalidateQueries({
        queryKey: [PAYRUN_QUERY_KEYS.GET_PAYRUN_WORKERS],
      });
    },
  });
};
